import { useCallback, useEffect, useState } from 'react'
import { Trash2 } from 'lucide-react'
import { apiErrorText } from '@/lib/api-error'
import { cardTitle, formCardSm, inlineInput } from '@/lib/form-styles'
import { fetchLvUnitMemory, rememberLvUnit } from '../api/calculation-lv.api'
import { fetchUnits } from '../api/calculation-positions.api'
import type { CalcUnit } from '../types/calculation.types'
import { knopfPrimaer, tabellenKopf } from './EinstellungsBausteine'

/**
 * AP-37 10., Reiter LV — die **gemerkten Einheiten-Übersetzungen** „Einheit
 * laut LV → Kürzel". Eine Liste für Kalkulation und Projekt (AP-35 Blatt 12,
 * `ProjectImportMapping kind = LV`). Ein Eintrag gilt ab dem nächsten Einlesen.
 */
export function LvEinheitenMerkliste({ darfAendern }: { darfAendern: boolean }) {
  const [liste, setListe] = useState<{ raw: string; code: string }[]>([])
  const [einheiten, setEinheiten] = useState<CalcUnit[]>([])
  const [roh, setRoh] = useState('')
  const [code, setCode] = useState('')
  const [fehler, setFehler] = useState<string | null>(null)

  const laden = useCallback(() => {
    fetchLvUnitMemory()
      .then(setListe)
      .catch((e) => setFehler(apiErrorText(e, 'Die Liste ließ sich nicht laden.')))
  }, [])
  useEffect(() => {
    laden()
    fetchUnits().then(setEinheiten).catch(() => setEinheiten([]))
  }, [laden])

  const schritt = async (r: string, c: string | null) => {
    try {
      setFehler(null)
      setListe(await rememberLvUnit(r, c))
    } catch (e) {
      setFehler(apiErrorText(e, 'Das hat nicht geklappt.'))
    }
  }

  return (
    <div className={`${formCardSm} max-w-2xl`}>
      <h3 className={cardTitle}>Einheiten-Übersetzung für LV-Dateien</h3>
      <p className="mt-1 text-xs text-muted-foreground">
        Gilt beim Einlesen in Kalkulation und Projekt. Gleiche Kürzel und übliche Schreibweisen (m2 → m², Stück → Stk) erkennt Symphenia von selbst.
      </p>
      {fehler && <p className="mt-2 text-sm text-destructive">{fehler}</p>}
      <table className="mt-3 w-full text-sm">
        <thead>
          <tr className={tabellenKopf}>
            <th className="py-1.5 pr-2">Einheit laut LV</th>
            <th className="py-1.5 pr-2">Einheit im Betrieb</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {liste.map((e) => (
            <tr key={e.raw} className="border-b border-border/60">
              <td className="py-1.5 pr-2 font-medium">{e.raw}</td>
              <td className="py-1.5 pr-2">
                <select className={`${inlineInput} !w-32`} value={e.code} disabled={!darfAendern} onChange={(ev) => void schritt(e.raw, ev.target.value)} aria-label={`Übersetzung für ${e.raw}`}>
                  {einheiten.map((u) => (
                    <option key={u.id} value={u.code}>
                      {u.code}
                    </option>
                  ))}
                </select>
              </td>
              <td className="py-1.5 text-right">
                {darfAendern && (
                  <button type="button" className="text-muted-foreground hover:text-destructive" title="vergessen" onClick={() => void schritt(e.raw, null)}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </td>
            </tr>
          ))}
          {liste.length === 0 && (
            <tr>
              <td colSpan={3} className="py-3 text-center text-xs text-muted-foreground">
                Noch nichts gemerkt — beim Übersetzen im Reiter LV einer Kalkulation entsteht der erste Eintrag.
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {darfAendern && (
        <div className="mt-3 flex flex-wrap items-end gap-2">
          <input className={`${inlineInput} !w-40`} placeholder="Einheit laut LV" value={roh} onChange={(e) => setRoh(e.target.value)} />
          <select className={`${inlineInput} !w-32`} value={code} onChange={(e) => setCode(e.target.value)} aria-label="Einheit im Betrieb">
            <option value="">—</option>
            {einheiten.map((u) => (
              <option key={u.id} value={u.code}>
                {u.code}
              </option>
            ))}
          </select>
          <button
            type="button"
            className={knopfPrimaer}
            disabled={!roh.trim() || !code}
            onClick={() =>
              void schritt(roh.trim(), code).then(() => {
                setRoh('')
                setCode('')
              })
            }
          >
            Anlegen
          </button>
        </div>
      )}
    </div>
  )
}
